/**
 * The non-React face of the session.
 *
 * Axios interceptors, the 401 handler and the query cache all live outside the React
 * tree, so they cannot call `useSession()`. This module is the seam between them and
 * the provider, and it points in ONE direction on purpose:
 *
 *   auth/  ──registers──▶  bridge  ◀──reads──  api/, tenant/
 *
 * `api/clients/*` imports the bridge; the bridge imports nothing. If auth imported
 * the query client to clear it, `auth` would depend on `api` and the two API clients
 * of §5.5 would gain a common ancestor that has no business existing.
 */

type ProvedorDeToken = () => Promise<string | null>
type Limpeza = () => void | Promise<void>

let provedorDeToken: ProvedorDeToken | null = null
let tratadorDeExpiracao: (() => void) | null = null
const limpezas = new Set<Limpeza>()

/**
 * The active adapter's token getter. `SessionProvider` registers it on mount.
 * Returns the unregister function so a remount cannot leave a stale provider behind.
 */
export function registrarProvedorDeToken(provedor: ProvedorDeToken): () => void {
  provedorDeToken = provedor
  return () => {
    if (provedorDeToken === provedor) provedorDeToken = null
  }
}

/**
 * Why "no token" is not one state — QA finding ARCH-003, story 0.6.
 *
 * `null` used to mean both "nobody is signed in" and "the refresh just failed",
 * and the caller could not tell them apart. The first should send an
 * unauthenticated request; the second must not send anything at all, because the
 * session is already dead. Collapsing them cost a wasted round-trip AND a second
 * expiry notification when the 401 came back.
 */
export type ResultadoDeToken =
  /** Signed in — put this on the Authorization header. */
  | { tipo: 'token'; token: string }
  /** Nobody is signed in. Legitimate: send the request with no header. */
  | { tipo: 'semSessao' }
  /** The refresh failed. The session is gone and expiry has ALREADY been notified. */
  | { tipo: 'sessaoExpirada' }

/**
 * A fresh, non-expired token for the Authorization header — **story 0.3 AC-4**.
 *
 * Story 0.6 calls this from the request interceptor of BOTH `apiUser` and `apiAdm`.
 * The adapter refreshes behind this call when the token is stale, so callers never
 * deal with expiry themselves.
 */
export async function obterTokenDeAcesso(): Promise<ResultadoDeToken> {
  if (!provedorDeToken) return { tipo: 'semSessao' }
  try {
    const token = await provedorDeToken()
    return token ? { tipo: 'token', token } : { tipo: 'semSessao' }
  } catch {
    // A refresh that fails IS an expired session. Notify once, here — and tell the
    // caller, so it does not fire a doomed request and trigger a second notification
    // off the resulting 401.
    notificarSessaoExpirada()
    return { tipo: 'sessaoExpirada' }
  }
}

export function registrarTratadorDeExpiracao(tratador: () => void): () => void {
  tratadorDeExpiracao = tratador
  return () => {
    if (tratadorDeExpiracao === tratador) tratadorDeExpiracao = null
  }
}

/**
 * Called when the session dies mid-use — **story 0.3 AC-5**. Story 0.6's response
 * interceptor calls this on a 401 so the user gets `/login` with a readable message
 * instead of a raw 401 rendered into a screen.
 */
export function notificarSessaoExpirada(): void {
  tratadorDeExpiracao?.()
}

/**
 * Register something to wipe on logout — **story 0.3 AC-10**.
 *
 * Story 0.5 registers the persisted tenant; story 0.6 registers `queryClient.clear()`.
 * Both are later stories' data, so logout inverts the dependency rather than
 * importing them.
 */
export function registrarLimpezaDeSessao(limpeza: Limpeza): () => void {
  limpezas.add(limpeza)
  return () => {
    limpezas.delete(limpeza)
  }
}

/**
 * Runs every registered cleanup. One failure must not skip the others.
 *
 * ⚠️ The `async` wrapper is load-bearing. `map(l => l())` lets a cleanup that
 * throws **synchronously** blow up the `map` before `allSettled` runs, which would
 * abort logout partway through — session cleared, query cache still full. Found by
 * story 0.5's test of the identical pattern in `tenant/trocaDeCliente.ts`; fixed in
 * both, because the shape is the bug rather than either instance of it.
 */
export async function executarLimpezaDeSessao(): Promise<void> {
  await Promise.allSettled([...limpezas].map(async (limpeza) => limpeza()))
}

/** Test seam — drops every registration so one test cannot leak into the next. */
export function reiniciarBridgeParaTeste(): void {
  provedorDeToken = null
  tratadorDeExpiracao = null
  limpezas.clear()
}
